/**
 * Date helpers for the inbox surface.
 *
 * The backend types `received_at` as a required number of Unix seconds,
 * but in practice malformed rows occasionally arrive with null / undefined
 * / NaN values. date-fns `format` throws "Invalid time value" on an
 * Invalid Date, which crashes the entire inbox render. These helpers
 * return safe placeholder values instead so a single bad row cannot take
 * down the page.
 */

import { format } from 'date-fns'

/**
 * Convert a Unix-seconds timestamp to a JavaScript Date, returning null
 * for anything that isn't a finite number or that produces an Invalid
 * Date (e.g. NaN, undefined, null, 0 is allowed since 1970 timestamps
 * are technically valid even if unlikely).
 */
export function unixSecondsToDate(value: unknown): Date | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  const date = new Date(value * 1000)
  if (Number.isNaN(date.getTime())) return null
  return date
}

/**
 * Format a Unix-seconds timestamp using a date-fns format string,
 * returning the supplied fallback (default em-dash) if the value is not
 * a valid timestamp.
 */
export function formatUnixSeconds(
  value: unknown,
  formatString: string,
  fallback: string = '—',
): string {
  const date = unixSecondsToDate(value)
  if (!date) return fallback
  return format(date, formatString)
}
